// frontend/src/pages/LandingPage/PrivacyPolicyPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, Database, Lock, Users, Globe, FileCheck, AlertCircle } from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';

const PrivacyPolicyPage = () => {
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);
    const currentDate = new Date().toLocaleDateString('pt-BR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    const sections = [
        {
            id: 1,
            title: 'Definições e Responsabilidade (LGPD)',
            content: 'O tratamento de dados na Plataforma Higeia.tech é estabelecido sob o princípio da clareza de papéis:',
            table: [
                {
                    role: 'Controlador',
                    responsible: 'Nosso Cliente (A Clínica, Hospital ou Organização de Saúde)',
                    definition: 'Define a finalidade e a base legal para o tratamento dos Dados de Saúde dos Pacientes (ex: tutela da saúde). O Controlador é responsável pelo consentimento do Paciente.'
                },
                {
                    role: 'Operador',
                    responsible: 'Higeia.tech',
                    definition: 'Atua exclusivamente processando e armazenando os Dados de Saúde, sob as instruções do Controlador e em estrita observância a esta Política e ao Contrato de Prestação de Serviços.'
                }
            ],
            contact: {
                title: 'Identificação - Higeia.tech',
                dpo: 'Para quaisquer dúvidas ou requisições relacionadas à privacidade e LGPD, entre em contato com nosso Encarregado de Dados (DPO) pelo e-mail: privacidade@higeia.tech.'
            },
            icon: Shield
        },
        {
            id: 2,
            title: 'Dados Coletados e Finalidade do Tratamento',
            content: 'Coletamos informações em duas categorias principais, sempre visando a segurança, eficiência e a qualidade do cuidado.',
            subsections: [
                {
                    title: 'A. Dados de Usuários Finais (Profissionais e Administradores)',
                    table: [
                        {
                            type: 'Identificação Profissional',
                            data: 'Nome, E-mail corporativo, CPF, Cargo, Número de registro CRM/COREN',
                            purpose: 'Gerenciamento de acesso e permissões na Plataforma (RBAC), rastreabilidade de ações (logs de auditoria) e comunicação essencial sobre o serviço.',
                            legalBasis: 'Execução de Contrato de Prestação de Serviços'
                        },
                        {
                            type: 'Dados de Uso',
                            data: 'Endereço IP, Data/Hora de Acesso, Ações Realizadas',
                            purpose: 'Segurança, prevenção a fraudes e garantia da rastreabilidade para auditoria e conformidade legal.',
                            legalBasis: 'Obrigação Legal e Interesses Legítimos (Segurança da Informação)'
                        }
                    ]
                },
                {
                    title: 'B. Dados de Pacientes (Dados Sensíveis)',
                    content: 'Como Operador, tratamos os seguintes Dados Sensíveis exclusivamente em nome e sob as instruções do Cliente (Controlador):',
                    items: [
                        {
                            subtitle: 'Prontuário e Saúde',
                            text: 'Histórico médico, alergias, sinais vitais, medicamentos, condições médicas, planos de cuidado.'
                        },
                        {
                            subtitle: 'Identificação do Paciente',
                            text: 'Nome completo, data de nascimento, informações de contato.'
                        }
                    ],
                    commitment: 'O Higeia.tech utiliza estes dados estritamente para o propósito de oferecer o Prontuário Eletrônico (PEP) e as ferramentas de gestão de cuidados. Em hipótese alguma o Higeia.tech comercializa, compartilha ou utiliza os Dados de Saúde para fins de marketing ou qualquer outra finalidade que não esteja diretamente ligada ao serviço contratado.'
                }
            ],
            icon: Database
        },
        {
            id: 3,
            title: 'Segurança, Armazenamento e Medidas Técnicas',
            content: 'Nosso compromisso com a segurança de dados sensíveis é um valor fundamental. Adotamos padrões de segurança de alto nível para proteger as informações contra acesso não autorizado, divulgação ou destruição.',
            items: [
                {
                    subtitle: 'Criptografia de Ponta a Ponta',
                    text: 'Todos os dados são criptografados em trânsito (via TLS/SSL) e em repouso (no armazenamento), garantindo que as informações sejam ilegíveis caso sejam interceptadas.'
                },
                {
                    subtitle: 'Controle de Acesso Rigoroso (RBAC)',
                    text: 'Utilizamos um sistema de controle de acesso baseado em função (Role-Based Access Control) para garantir que apenas os Usuários Finais autorizados pelo Cliente tenham acesso aos dados estritamente necessários para o desempenho de suas funções.'
                },
                {
                    subtitle: 'Hospedagem e Retenção',
                    text: 'Os dados são armazenados em infraestrutura de nuvem segura com localização preferencialmente no Brasil, em total conformidade com os requisitos de privacidade e segurança.'
                },
                {
                    subtitle: 'Retenção e Exclusão',
                    text: 'Os dados são retidos enquanto o contrato com o Cliente estiver ativo. Após a rescisão, o Cliente terá um período de 30 dias para realizar a portabilidade ou o backup integral. Após este prazo, os dados serão permanentemente excluídos de forma segura, seguindo as diretrizes legais e contratuais.'
                }
            ],
            icon: Lock
        },
        {
            id: 4,
            title: 'Compartilhamento de Dados',
            content: 'O Higeia.tech prioriza a confidencialidade. O compartilhamento ocorre apenas sob circunstâncias estritamente controladas:',
            items: [
                {
                    subtitle: 'Sub-operadores Essenciais',
                    text: 'Podemos compartilhar dados com parceiros de infraestrutura (provedores de cloud) ou serviços técnicos vitais (ex: monitoramento de performance), desde que estes parceiros estejam contratualmente vinculados a cumprir as mesmas diretrizes rigorosas de segurança e LGPD que o Higeia.tech.'
                },
                {
                    subtitle: 'Obrigação Legal',
                    text: 'Os dados podem ser divulgados mediante requerimento de autoridades competentes em virtude de obrigação legal, notificação judicial ou regulatória.'
                },
                {
                    subtitle: 'Instrução do Cliente',
                    text: 'O compartilhamento ou portabilidade dos dados pode ocorrer sob a instrução expressa e formal do Cliente (Controlador).'
                }
            ],
            icon: Globe
        },
        {
            id: 5,
            title: 'Direitos dos Titulares de Dados',
            content: 'O Higeia.tech se compromete a auxiliar o Cliente (Controlador) no atendimento aos direitos dos Titulares (Pacientes e Usuários Finais), conforme o Art. 18 da LGPD.',
            items: [
                {
                    subtitle: 'Direitos Incluídos',
                    text: 'Confirmação da existência de tratamento, acesso aos dados, correção, anonimização e portabilidade.'
                },
                {
                    subtitle: 'Mecanismo de Requisição',
                    text: 'As requisições devem ser formalizadas através do canal de contato do DPO, garantindo que o processo siga os protocolos de segurança e autenticação.'
                }
            ],
            icon: Shield
        },
        {
            id: 6,
            title: 'Alterações desta Política',
            content: 'Esta Política de Privacidade pode ser atualizada a qualquer momento para refletir mudanças em nossos serviços, tecnologia ou legislação aplicável. Envidaremos todos os esforços para notificar os Clientes sobre alterações significativas, e a versão mais recente estará sempre disponível nesta página, contendo a data da última atualização.',
            icon: AlertCircle
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main className="py-12 sm:py-16">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    
                    {/* Cabeçalho */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-higeia-light rounded-full mb-6">
                            <Shield className="h-8 w-8 text-higeia-dark" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
                            Política de{' '}
                            <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                Privacidade
                            </span>
                        </h1>
                        <p className="text-gray-600">
                            Última atualização: {currentDate}
                        </p>
                    </motion.div>

                    {/* Aviso Importante */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="bg-gradient-to-r from-higeia-dark to-higeia-medium rounded-2xl p-6 text-white mb-12 shadow-xl"
                    >
                        <div className="flex items-start gap-4">
                            <Shield className="h-6 w-6 flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="font-bold text-lg mb-2">Sua Confiança é Nossa Prioridade</h3>
                                <p className="text-white/90 text-sm leading-relaxed">
                                    A sua confiança e a segurança dos dados de seus pacientes são a nossa prioridade. Esta Política de Privacidade descreve como o Higeia.tech, uma plataforma SaaS de Gestão de Prontuários Eletrônicos e Cuidados, coleta, usa, armazena e protege as informações em total conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Seções da Política */}
                    <div className="space-y-8">
                        {sections.map((section, index) => {
                            const Icon = section.icon;
                            return (
                                <motion.div
                                    key={section.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                                    className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                                >
                                    {/* Título da Seção */}
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex-shrink-0 w-12 h-12 bg-higeia-light rounded-xl flex items-center justify-center">
                                            <Icon className="h-6 w-6 text-higeia-dark" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900">
                                            {section.id}. {section.title}
                                        </h2>
                                    </div>

                                    {/* Conteúdo da Seção */}
                                    <div className="prose prose-gray max-w-none">
                                        {/* Conteúdo introdutório */}
                                        {section.content && (
                                            <p className="text-gray-700 leading-relaxed mb-6">
                                                {section.content}
                                            </p>
                                        )}

                                        {/* Tabela de Definições (Seção 1) */}
                                        {section.table && !section.subsections && (
                                            <div className="overflow-x-auto mb-6">
                                                <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
                                                    <thead className="bg-higeia-dark text-white">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left font-bold">Papel na LGPD</th>
                                                            <th className="px-4 py-3 text-left font-bold">Responsável</th>
                                                            <th className="px-4 py-3 text-left font-bold">Definição no Higeia.tech</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {section.table.map((row, idx) => (
                                                            <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                <td className="px-4 py-3 border border-gray-200 font-semibold text-gray-900">
                                                                    {row.role}
                                                                </td>
                                                                <td className="px-4 py-3 border border-gray-200 text-gray-700">
                                                                    {row.responsible}
                                                                </td>
                                                                <td className="px-4 py-3 border border-gray-200 text-gray-700">
                                                                    {row.definition}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* Contato DPO (Seção 1) */}
                                        {section.contact && (
                                            <div className="bg-higeia-light/30 rounded-xl p-6 mb-6 border border-higeia-light">
                                                <h4 className="font-bold text-gray-900 mb-2">
                                                    {section.contact.title}
                                                </h4>
                                                <p className="text-gray-700 leading-relaxed">
                                                    {section.contact.dpo}
                                                </p>
                                            </div>
                                        )}

                                        {/* Lista de itens com subtítulos */}
                                        {section.items && !section.subsections && (
                                            <div className="space-y-6">
                                                {section.items.map((item, idx) => (
                                                    <div key={idx} className="border-l-4 border-higeia-light pl-4">
                                                        <h4 className="font-bold text-gray-900 mb-2">
                                                            {item.subtitle}
                                                        </h4>
                                                        <p className="text-gray-700 leading-relaxed">
                                                            {item.text}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Subseções (Seção 2 - Dados Coletados) */}
                                        {section.subsections && (
                                            <div className="space-y-8">
                                                {section.subsections.map((subsection, idx) => (
                                                    <div key={idx} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                                        <h3 className="font-bold text-lg text-gray-900 mb-4">
                                                            {subsection.title}
                                                        </h3>
                                                        
                                                        {/* Tabela de Dados (para Dados de Usuários) */}
                                                        {subsection.table && (
                                                            <div className="overflow-x-auto mb-4">
                                                                <table className="w-full border-collapse border border-gray-300 rounded-lg overflow-hidden bg-white">
                                                                    <thead className="bg-higeia-dark text-white">
                                                                        <tr>
                                                                            <th className="px-4 py-3 text-left font-bold text-sm">Tipo de Dado Coletado</th>
                                                                            <th className="px-4 py-3 text-left font-bold text-sm">Finalidade</th>
                                                                            <th className="px-4 py-3 text-left font-bold text-sm">Base Legal (LGPD)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {subsection.table.map((row, rowIdx) => (
                                                                            <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                                <td className="px-4 py-3 border border-gray-300">
                                                                                    <div className="font-semibold text-gray-900 mb-1">{row.type}</div>
                                                                                    <div className="text-sm text-gray-600">{row.data}</div>
                                                                                </td>
                                                                                <td className="px-4 py-3 border border-gray-300 text-gray-700 text-sm">
                                                                                    {row.purpose}
                                                                                </td>
                                                                                <td className="px-4 py-3 border border-gray-300 text-gray-700 text-sm">
                                                                                    {row.legalBasis}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}

                                                        {/* Conteúdo de texto */}
                                                        {subsection.content && (
                                                            <p className="text-gray-700 leading-relaxed mb-4">
                                                                {subsection.content}
                                                            </p>
                                                        )}

                                                        {/* Lista de itens */}
                                                        {subsection.items && (
                                                            <div className="space-y-4">
                                                                {subsection.items.map((item, itemIdx) => (
                                                                    <div key={itemIdx} className="border-l-4 border-higeia-light pl-4">
                                                                        <h4 className="font-semibold text-gray-900 mb-1">
                                                                            {item.subtitle}
                                                                        </h4>
                                                                        <p className="text-gray-700 leading-relaxed">
                                                                            {item.text}
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Compromisso (para Dados de Pacientes) */}
                                                        {subsection.commitment && (
                                                            <div className="mt-6 bg-white rounded-lg p-4 border-2 border-higeia-light">
                                                                <p className="text-gray-800 leading-relaxed font-medium">
                                                                    <strong>Compromisso de Finalidade:</strong> {subsection.commitment}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Rodapé do Documento */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.6 }}
                        className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                    >
                        <div className="text-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">
                                Dúvidas sobre Privacidade?
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Para exercer seus direitos ou esclarecer dúvidas sobre o tratamento de seus dados pessoais, 
                                entre em contato conosco através do e-mail:
                            </p>
                            <a 
                                href="mailto:privacidade@higeia.tech"
                                className="inline-flex items-center gap-2 text-higeia-dark font-semibold hover:text-higeia-medium transition-colors"
                            >
                                <Mail className="h-5 w-5" />
                                privacidade@higeia.tech
                            </a>
                        </div>
                        
                        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                            <p className="text-sm text-gray-500">
                                Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)
                            </p>
                        </div>
                    </motion.div>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default PrivacyPolicyPage;

